import { omit } from 'ts-utils';
import { Analytics } from '../../../util/Analytics';
import { Auth, RegistrationDetails } from '../../../util/Auth';
import { ActionType } from '../../ActionTypes';
import { AppSelector, store as baseStore } from '../../Store';
import { CompletedUser, IncompleteUser } from '../UserState';
import { handleUserError } from '../Utils';

export type FinishTempRegisterDetails = Pick<RegistrationDetails, 'name' | 'password'>;

/** Updates the user's name and password to complete the registration. */
export async function finishTemporaryUserRegistration(
  details: FinishTempRegisterDetails,
  store = baseStore
): Promise<void> {
  try {
    store.dispatch({ type: ActionType.FINISH_TEMPORARY_REGISTER_USER_LOADING });

    const { name } = details;
    let user = AppSelector.user(store.getState());

    try {
      await updateNameAndPassword(details);
    } catch (error) {
      const requiresLogin = Auth.isAuthError(error) && error.code === 'auth/requires-recent-login';
      const tempPassword = user.type === 'partial' ? user.tempPassword : undefined;
      if (requiresLogin && tempPassword !== undefined && user?.email !== undefined) {
        const { user: authUser } = await Auth.signInUser({
          email: user.email,
          password: tempPassword,
        });
        user = { ...user, ...authUser };
        await updateNameAndPassword(details);
      } else {
        throw error;
      }
    }

    Analytics.identify(
      { userId: user.userId, name, email: user.email },
      Analytics.Event.a_signup_succeeded
    );

    store.dispatch({
      type: ActionType.FINISH_TEMPORARY_REGISTER_USER_SUCCESS,
      user: {
        ...omit(user as IncompleteUser, ['tempPassword']),
        type: 'email',
        name,
        setUserPassword: false,
      } as CompletedUser,
    });
  } catch (rawError) {
    const error = handleUserError(rawError);
    store.dispatch({
      type: ActionType.FINISH_TEMPORARY_REGISTER_USER_FAILURE,
      error,
    });
  }
}

async function updateNameAndPassword(details: FinishTempRegisterDetails) {
  await Promise.all([
    Auth.updateCurrentUserName(details.name),
    Auth.updateCurrentUserPassword(details.password),
  ]);
}
